import { Typography, Badge, Box } from '@lucid/lego';
import type { FilterOption, FilterState } from '@lucid/lego';
import { ProspectActivityType } from '../../../../api/services/lupp/prospect/dto.prospect';
import type {
  Prospect,
  ProspectSearchFilters,
  ListMetadataOption,
  ListMetadataLocation,
} from '../../../../api/services/lupp/prospect/dto.prospect';
import { escapeHtml, formatDateTime, formatString } from '../../../../utils/tools';
import { isEngagementHubEnabled } from '../../../../config/featureFlags';
import { getActivityTypeLabel, getCallConsentBadge, getEmailConsentBadge } from '../../constants';
import styles from './utils.module.scss';

export const getStatusBadgeColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'open':
      return 'blue';
    case 'contacting':
      return 'orange';
    case 'engaged':
      return 'green';
    case 'converted':
      return 'fuchsia';
    case 'quote generated':
    case 'quotes generated':
      return 'indigo';
    case 'quote accepted':
    case 'quotes accepted':
      return 'purple';
    case 'ordered':
      return 'teal';
    case 'delivered':
      return 'darktaupe';
    case 'closed':
      return 'gray';
    default:
      return 'gray';
  }
};

export const getPriorityBadgeColor = (priority: string) => {
  switch (priority.toLowerCase()) {
    case 'high':
      return 'error';
    case 'medium':
      return 'informative';
    case 'low':
      return 'warning';
    default:
      return 'neutral';
  }
};

export const getInterestedModelDisplayName = (model: string) => {
  switch (model?.toLowerCase()) {
    case 'lucid-air':
      return 'Lucid Air';
    case 'lucid-gravity':
      return 'Lucid Gravity';
    default:
      return model;
  }
};

/*
|--------------------------------------------------------------------------
| PROSPECT FILTERS <-> lego Table FilterState mapping (AUCTUS-5304)
|--------------------------------------------------------------------------
*/

/** Converts list-metadata status/source options into lego Table filter dropdown options. */
export const toMetadataFilterOptions = (options?: ListMetadataOption[]): FilterOption[] =>
  (options ?? []).map((option) => ({ value: option.value, label: formatString(option.value) }));

/** Converts list-metadata agency/studio locations into lego Table filter dropdown options. */
export const toLocationFilterOptions = (locations?: ListMetadataLocation[]): FilterOption[] =>
  (locations ?? []).map((location) => ({
    value: location.locationIdentifier,
    label: location.city ? `${location.locationName} (${location.city})` : location.locationName,
  }));

/** Date-only filter input ("YYYY-MM-DD") -> inclusive start-of-day ISO bound; passes full datetimes through. */
const toIsoStartOfDay = (value?: string): string | undefined => {
  if (!value) return undefined;
  return value.includes('T') ? value : `${value}T00:00:00Z`;
};

/** Date-only filter input ("YYYY-MM-DD") -> inclusive end-of-day ISO bound; passes full datetimes through. */
const toIsoEndOfDay = (value?: string): string | undefined => {
  if (!value) return undefined;
  return value.includes('T') ? value : `${value}T23:59:59Z`;
};

/** Builds the lego Table's controlled FilterState from the prospect search filters we send to the API. */
export const buildFilterState = (filters: ProspectSearchFilters): FilterState => ({
  statusName: filters.statusNames?.length ? filters.statusNames : null,
  location: filters.locationIds?.length ? filters.locationIds : null,
  prospectSourceName: filters.sourceNames?.length ? filters.sourceNames : null,
  assignedOwner: filters.assignedOwner || null,
  createdDatetime:
    filters.createdFrom || filters.createdTo
      ? { from: filters.createdFrom, to: filters.createdTo }
      : null,
});

/** Parses the lego Table's FilterState (committed on Apply) back into prospect search filters. */
export const parseFilterState = (filterState: FilterState): ProspectSearchFilters => {
  const statusValue = filterState.statusName;
  const locationValue = filterState.location;
  const sourceValue = filterState.prospectSourceName;
  const ownerValue = filterState.assignedOwner;
  const dateValue = filterState.createdDatetime;
  const dateRange =
    dateValue && typeof dateValue === 'object' && !Array.isArray(dateValue) ? dateValue : null;

  return {
    statusNames: Array.isArray(statusValue) ? statusValue : undefined,
    locationIds: Array.isArray(locationValue) ? locationValue : undefined,
    sourceNames: Array.isArray(sourceValue) ? sourceValue : undefined,
    assignedOwner: typeof ownerValue === 'string' && ownerValue ? ownerValue : undefined,
    createdFrom: toIsoStartOfDay(dateRange?.from != null ? String(dateRange.from) : undefined),
    createdTo: toIsoEndOfDay(dateRange?.to != null ? String(dateRange.to) : undefined),
  };
};

interface FieldType {
  label: string;
  key: string;
  value: string | number | boolean | null | string[];
}

interface SectionType {
  label: string;
  fields: FieldType[];
}

export interface ProspectDetailType {
  prospectIdentifier: string;
  prospectName: string;
  situation?: SectionType;
  prospectStatusProgression?: SectionType;
  purchaseProfile?: SectionType;
  keyFields?: SectionType;
  information?: SectionType;
}

interface CreateColumnsParams {
  theme: 'light' | 'dark';
  onEditClick: (record: Prospect) => void;
  labels: Record<string, string>;
  locale?: string;
  /** When true, renders the importer read-only view: hides Status, Next Engagement Date
   *  and Assigned Owner columns, and removes the clickable link from Full Name. */
  isImporter?: boolean;
  /** Filter dropdown options sourced from GET /list-metadata?module=PROSPECT (AUCTUS-5304) */
  filterOptions?: {
    statuses: FilterOption[];
    sources: FilterOption[];
    locations: FilterOption[];
  };
}

const getActivityText = (
  activity: NonNullable<Prospect['prospectActivity']>,
  labels?: Record<string, string>
) => {
  const rawActivityType = getActivityTypeLabel(
    activity.activityType,
    labels,
    formatString(activity.activityType?.trim()) || ''
  );
  // Location change details are shown in the Engagement Hub only; the listing shows the type.
  if (activity.activityType === ProspectActivityType.LOCATION_CHANGED) {
    return rawActivityType || '-';
  }
  const rawActivityDescription = activity.activityDescription?.trim() || '';

  return rawActivityType && rawActivityDescription
    ? `${rawActivityType}: ${rawActivityDescription}`
    : rawActivityType || rawActivityDescription || '-';
};

export const createColumns = ({
  theme,
  onEditClick,
  labels,
  locale = 'en-US',
  isImporter = false,
  filterOptions,
}: CreateColumnsParams) => {
  /** Keys hidden in the importer read-only view (per AC for AUCTUS-1983) */
  const IMPORTER_HIDDEN_COLUMNS = new Set([
    'statusName',
    'nextEngagementDate',
    'assignedOwner',
    'lastActivity',
  ]);

  const allColumns = [
    {
      key: 'statusName',
      title: labels?.status || 'Status',
      width: '100px',
      align: 'left' as const,
      sortable: true,
      filter: {
        type: 'multiselect' as const,
        options: filterOptions?.statuses ?? [],
        placeholder: labels?.['table.filterStatusPlaceholder'] ?? 'Select status',
      },
      render: (_: unknown, record: Prospect) => {
        const status = record.statusName?.toLowerCase() || 'open';
        // Map status to localized label
        const statusLabelMap: Record<string, string> = {
          open: labels?.['const.PROSPECT_STATUS_STEPS.open'] ?? 'Open',
          contacting: labels?.['const.PROSPECT_STATUS_STEPS.contacting'] ?? 'Contacting',
          engaged: labels?.['const.PROSPECT_STATUS_STEPS.engaged'] ?? 'Engaged',
          'quote generated':
            labels?.['const.PROSPECT_STATUS_STEPS.quoteGenerated'] ?? 'Quote generated',
          'quote accepted':
            labels?.['const.PROSPECT_STATUS_STEPS.quoteAccepted'] ?? 'Quote accepted',
          ordered: labels?.['const.PROSPECT_STATUS_STEPS.ordered'] ?? 'Ordered',
          delivered: labels?.['const.PROSPECT_STATUS_STEPS.delivered'] ?? 'Delivered',
          closed: labels?.['const.PROSPECT_STATUS_STEPS.closed'] ?? 'Closed',
        };
        const statusLabel =
          statusLabelMap[status] ?? status.charAt(0).toUpperCase() + status.slice(1);
        return (
          <Badge
            label={statusLabel}
            color={getStatusBadgeColor(status)}
            type="secondary"
            theme={theme === 'dark' ? 'light' : 'dark'}
            minWidth="60px"
          />
        );
      },
    },
    {
      key: 'prospectFirstName',
      title: labels.full_name || 'Full Name',
      width: '150px',
      sortable: true,
      render: (_: unknown, record: Prospect) => {
        const fullName =
          `${escapeHtml(record.prospectFirstName || '')} ${escapeHtml(record.prospectLastName || '')}`.trim() ||
          '-';
        if (isImporter) {
          return (
            <Typography variant="body" size="small" theme={theme} color="black">
              {fullName}
            </Typography>
          );
        }
        return (
          <Typography
            variant="body"
            size="small"
            theme={theme}
            color="link"
            onClick={() => onEditClick(record)}
          >
            {fullName}
          </Typography>
        );
      },
    },
    {
      key: 'isCallOptIn',
      title: labels?.['util.phoneConsent'] ?? 'Phone Consent',
      width: '150px',
      sortable: false,
      render: (_: unknown, record: Prospect) => (
        <Badge
          {...getCallConsentBadge(record, labels)}
          theme={theme === 'dark' ? 'light' : 'dark'}
        />
      ),
    },
    {
      key: 'isEmailOptIn',
      title: labels?.['util.emailConsent'] ?? 'Email Consent',
      width: '150px',
      sortable: false,
      render: (_: unknown, record: Prospect) => (
        <Badge
          {...getEmailConsentBadge(record, labels)}
          theme={theme === 'dark' ? 'light' : 'dark'}
        />
      ),
    },
    {
      key: 'prospectEmail',
      title: labels.email || 'Email',
      width: '200px',
      sortable: true,
      render: (_: unknown, record: Prospect) => (
        <Typography variant="body" size="small" theme={theme} color="black">
          {escapeHtml(record.prospectEmail || '-')}
        </Typography>
      ),
    },
    {
      key: 'prospectPhone',
      title: labels.phone || 'Phone',
      width: '150px',
      sortable: true,
      render: (_: unknown, record: Prospect) => (
        <Typography variant="body" size="small" theme={theme} color="black">
          {escapeHtml(record.prospectPhone || '-')}
        </Typography>
      ),
    },
    {
      key: 'zipCode',
      title: labels.postal_code || 'Postal Code',
      width: '120px',
      sortable: true,
      render: (_: unknown, record: Prospect) => (
        <Typography variant="body" size="small" theme={theme} color="black">
          {escapeHtml(record.zipCode || '-')}
        </Typography>
      ),
    },
    {
      key: 'city',
      title: labels?.city || 'City',
      width: '150px',
      sortable: true,
      render: (_: unknown, record: Prospect) => (
        <Typography variant="body" size="small" theme={theme} color="black">
          {escapeHtml(record.city || record.region || '-')}
        </Typography>
      ),
    },
    {
      key: 'location',
      title: labels?.['util.location'] ?? 'Location',
      width: '150px',
      sortable: true,
      filter: {
        type: 'multiselect' as const,
        options: filterOptions?.locations ?? [],
        placeholder: labels?.['table.filterLocationPlaceholder'] ?? 'Select location',
      },
      render: (_: unknown, record: Prospect) => (
        <Typography variant="body" size="small" theme={theme} color="black">
          {escapeHtml(
            record.locations
              ?.map((location) => location.locationName)
              .filter(Boolean)
              .join(', ') || '-'
          )}
        </Typography>
      ),
    },
    {
      key: 'lastActivity',
      title: labels?.['util.lastActivity'] ?? 'Last Activity',
      sortable: false,
      render: (_: unknown, record: Prospect) => {
        if (record.prospectActivity == null) {
          return (
            <Typography variant="body" size="small" theme={theme} color="black">
              {labels?.['util.noActivityYet'] ?? 'No activity yet'}
            </Typography>
          );
        }

        const rawActivityText = getActivityText(record.prospectActivity, labels);
        const activityText = escapeHtml(
          rawActivityText.length > 88 ? `${rawActivityText.slice(0, 88)}...` : rawActivityText
        );
        const activityTimestampText = record.prospectActivity.activityTimestamp
          ? formatDateTime(record.prospectActivity.activityTimestamp, locale)
          : '-';

        return (
          <Box className={styles.lastActivityText}>
            <Typography variant="body" size="small" theme={theme} color="black">
              {activityText}
            </Typography>
            <Typography variant="body" size="extraSmall" theme={theme} color="gray-6">
              {activityTimestampText}
            </Typography>
          </Box>
          // <Box className={styles.lastActivityRow}>
          //   <Box className={styles.lastActivityText}>
          //     <Typography variant="body" size="small" theme={theme} color="black">
          //       {activityText}
          //     </Typography>
          //     <Typography variant="body" size="extraSmall" theme={theme} color="gray-6">
          //       {activityTimestampText}
          //     </Typography>
          //   </Box>
          //   {record.prospectActivity.activityReadTimestamp == null ? (
          //     <Box className={styles.lastActivityCircle} aria-hidden="true" />
          //   ) : null}
          // </Box>
        );
      },
    },
    {
      key: 'shortCode',
      title: labels?.['util.shortCode'] ?? 'Shortcode',
      width: '120px',
      sortable: false,
      render: (_: unknown, record: Prospect) => (
        <Typography variant="body" size="small" theme={theme} color="black">
          {escapeHtml(record.prospectActivityDetail?.shortCode || '-')}
        </Typography>
      ),
    },
    {
      key: 'prospectSourceName',
      title: labels.source_of_prospect || 'Source of Prospect',
      width: '150px',
      sortable: true,
      filter: {
        type: 'multiselect' as const,
        options: filterOptions?.sources ?? [],
        placeholder: labels?.['table.filterSourcePlaceholder'] ?? 'Select source',
      },
      render: (_: unknown, record: Prospect) => (
        <Typography variant="body" size="small" theme={theme} color="black">
          {escapeHtml(
            record.prospectCaptureForm || record.prospectSourceName || record.sourceName || '-'
          )}
        </Typography>
      ),
    },
    {
      key: 'comment',
      title: labels?.['util.comment'] ?? 'Comments',
      width: '200px',
      sortable: false,
      render: (_: unknown, record: Prospect) => (
        <Typography variant="body" size="small" theme={theme} color="black">
          {escapeHtml(record.prospectActivityDetail?.comments || '-')}
        </Typography>
      ),
    },
    {
      key: 'nextEngagementDate',
      title: labels.next_engagement_date || 'Next Engagement Date',
      width: '180px',
      sortable: true,
      render: (_: unknown, record: Prospect) => (
        <Typography variant="body" size="small" theme={theme} color="black">
          {record.nextEngagementDate
            ? new Date(record.nextEngagementDate).toLocaleDateString(locale, {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })
            : '-'}
        </Typography>
      ),
    },
    {
      key: 'createdDatetime',
      title: labels?.['util.createdDate'] ?? 'Created Date',
      width: '150px',
      sortable: true,
      filter: {
        type: 'dateRange' as const,
      },
      render: (_: unknown, record: Prospect) => (
        <Typography variant="body" size="small" theme={theme} color="black">
          {record.createdDatetime
            ? new Date(record.createdDatetime).toLocaleDateString(locale, {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })
            : '-'}
        </Typography>
      ),
    },
    {
      key: 'assignedOwner',
      title: labels?.['util.assignedOwner'] ?? 'Assigned Owner',
      width: '150px',
      sortable: true,
      filter: {
        type: 'text' as const,
        placeholder: labels?.['table.filterOwnerPlaceholder'] ?? 'Search owner',
      },
      render: (_: unknown, record: Prospect) => (
        <Typography variant="body" size="small" theme={theme} color="black">
          {escapeHtml(record.assignedOwner || '-')}
        </Typography>
      ),
    },
  ];

  const hiddenColumns = new Set<string>(isImporter ? IMPORTER_HIDDEN_COLUMNS : []);
  // Latest Activity column is hidden until the Engagement Hub feature flag is enabled.
  if (!isEngagementHubEnabled()) {
    hiddenColumns.add('lastActivity');
  }
  // Shortcode and Comment columns are only relevant for importer users.
  if (!isImporter) {
    hiddenColumns.add('shortCode');
    hiddenColumns.add('comment');
    hiddenColumns.add('isCallOptIn');
    hiddenColumns.add('isEmailOptIn');
  }

  return hiddenColumns.size > 0
    ? allColumns.filter((col) => !hiddenColumns.has(col.key))
    : allColumns;
};

export const createProspectDetail = (
  record: Prospect,
  labels: Record<string, string> = {}
): ProspectDetailType => ({
  prospectIdentifier: String(record.prospectIdentifier),
  prospectName: `${record.prospectFirstName || '-'} ${record.prospectLastName || '-'}`,
  situation: {
    label: labels.situation,
    fields: [
      {
        label: labels.situational_notes,
        key: 'noteText',
        value: record.noteText || labels.default_placeholder,
      },
      {
        label: labels.objections,
        key: 'objections',
        value: record.objections || labels.default_placeholder,
      },
      {
        label: labels.important_features,
        key: 'importantFeatures',
        value: record.importantFeatures || labels.default_placeholder,
      },
    ],
  },
  purchaseProfile: {
    label: labels.purchase_profile,
    fields: [
      {
        label: labels.interested_model,
        key: 'interestedModel',
        value: record.interestedModel || (labels.default_placeholder as string),
      },
      {
        label: labels.interested_trim,
        key: 'interestedTrim',
        value: record.interestedTrim || (labels.default_placeholder as string),
      },
      {
        label: labels.purchase_type,
        key: 'purchaseType',
        value: record.purchaseType || (labels.default_placeholder as string),
      },
      {
        label: labels.purchase_timing,
        key: 'purchaseTiming',
        value: record.purchaseTiming || (labels.default_placeholder as string),
      },
      {
        label: labels.finance_type,
        key: 'financeType',
        value: record.financeType || (labels.default_placeholder as string),
      },
    ],
  },
  information: {
    label: labels.information,
    fields: [
      {
        label: labels.company_name,
        key: 'companyName',
        value: record.prospectCompanyName || labels.default_placeholder,
      },
      {
        label: labels.preferred_contact_method,
        key: 'preferredContactMethod',
        value: record.preferredContactMethod || labels.default_placeholder,
      },
      {
        label: labels.preferred_language,
        key: 'preferredLanguageIso',
        value: record.preferredLanguageIso || labels.default_placeholder,
      },
    ],
  },
});
